import { buildFeesAndChargesBreakdown } from "../components/CommissionStatementPDF/utils/feesAndChargesBreakdown";
import { dedupeCommissionRows } from "../components/CommissionStatementPDF/utils/commissionBreakdown";
import { buildPaymentSummary } from "./paymentSummary";
import { manualPaymentFeeEntries } from "./manualPaymentCharges";
import { paymentsForMonth } from "./paymentDates";
import { mappedIoIntroducerGroups } from "./ioRecipientTargets";
import {
  groupUnmatchedIoPayments,
  ioRecipientUserId,
} from "./unmatchedIoUsers";
import { normaliseQuilterRows } from "./quilterStatementParser";

const keyOf = (value) => String(value || "").trim().toLowerCase();
const amountOf = (row) => {
  const amount = Number(String(row?.PaymentAmount ?? 0).replace(/[,£]/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};
const commissionTotal = (rows, selectedMonth, source) =>
  dedupeCommissionRows(
    rows.map((row) => ({
      ...row,
      __source: source,
      __statementMonth: selectedMonth,
    })),
  ).reduce((total, row) => total + amountOf(row), 0);

const outputRow = ({
  category,
  name,
  identifier,
  paymentCount,
  openingBalance = 0,
  totalCommission = 0,
  feesAndCharges = buildFeesAndChargesBreakdown([]),
}) => {
  const summary = buildPaymentSummary({
    openingBalance,
    totalCommission,
    feesAndCharges,
  });
  return {
    Category: category,
    Name: name,
    Identifier: identifier,
    "Payment Count": Number(paymentCount || 0),
    "Opening Balance": summary.openingBalance,
    "Total Commission": summary.totalCommission,
    "Total Charged": summary.totalCharged,
    Payments: summary.payments,
    Advances: summary.advances,
    "Closing Balance": summary.balanceToPay,
  };
};

export const buildUnknownUsersCommissionRows = ({
  selectedMonth,
  ioRows = [],
  quilterStatement,
  manualPayments = [],
  advisers = [],
  ioRecipientMappings = {},
  matchedIoRecipientIds = [],
}) => {
  const rows = [];
  const advisersByName = new Map(
    advisers.map((adviser) => [keyOf(adviser.name), adviser]),
  );
  const quilterLinks = new Map(
    (quilterStatement?.adviserLinks || []).map((link) => [
      keyOf(link.sourceAdviserName),
      keyOf(link.email),
    ]),
  );
  const unmatchedQuilter = new Map();
  normaliseQuilterRows(quilterStatement?.rows || []).forEach((entry) => {
    const sourceKey = keyOf(
      entry.SourceAdviserName || entry.MappedAdviserName,
    );
    const linkedEmail =
      quilterLinks.get(sourceKey) ||
      keyOf(advisersByName.get(keyOf(entry.MappedAdviserName))?.email);
    if (!sourceKey || linkedEmail) return;
    const current = unmatchedQuilter.get(sourceKey) || {
      name:
        entry.MappedAdviserName ||
        entry.SourceAdviserName ||
        "Unmatched Quilter Adviser",
      entries: [],
    };
    current.entries.push(entry);
    unmatchedQuilter.set(sourceKey, current);
  });
  unmatchedQuilter.forEach((item, sourceKey) => {
    rows.push(outputRow({
      category: "Unmatched Quilter Advisers",
      name: item.name,
      identifier: sourceKey,
      paymentCount: item.entries.length,
      totalCommission: commissionTotal(
        item.entries,
        selectedMonth,
        "quilter",
      ),
    }));
  });

  mappedIoIntroducerGroups(ioRows, ioRecipientMappings).forEach((item) => {
    const manualEntries = paymentsForMonth(manualPayments, selectedMonth).filter(
      (payment) =>
        keyOf(payment.mappedEntityType) === "introducer" &&
        keyOf(payment.mappedEntityId || payment.ioIntroducerId) ===
          keyOf(item.targetId),
    );
    const openingBalance = manualEntries
      .filter((payment) => payment.paymentType === "opening_balance")
      .reduce(
        (total, payment) =>
          total + Number(payment.openingBalanceAmount || 0),
        0,
      );
    const recipientIds = new Set(item.recipientIds.map(keyOf));
    const commissionEntries = ioRows.filter((entry) =>
      recipientIds.has(keyOf(ioRecipientUserId(entry))),
    );
    rows.push(outputRow({
      category: "IO Introducers",
      name: item.name,
      identifier: item.targetId,
      paymentCount: item.paymentCount,
      openingBalance,
      totalCommission: commissionTotal(
        commissionEntries,
        selectedMonth,
        "io",
      ),
      feesAndCharges: buildFeesAndChargesBreakdown(
        manualPaymentFeeEntries(manualEntries),
      ),
    }));
  });

  const unknownPayments = new Map();
  paymentsForMonth(manualPayments, selectedMonth)
    .filter(
      (payment) =>
        keyOf(payment.mappedEntityType) === "unknown",
    )
    .forEach((payment) => {
      const identifier = String(payment.mappedEntityId || "").trim();
      if (!identifier) return;
      const current = unknownPayments.get(identifier) || {
        name: String(
          payment.mappedEntityName || payment.sourceName || "Unknown",
        ).trim(),
        entries: [],
      };
      current.entries.push(payment);
      unknownPayments.set(identifier, current);
    });
  unknownPayments.forEach((item, identifier) => {
    const openingBalance = item.entries
      .filter((payment) => payment.paymentType === "opening_balance")
      .reduce(
        (total, payment) =>
          total + Number(payment.openingBalanceAmount || 0),
        0,
      );
    rows.push(outputRow({
      category: "Unknown",
      name: item.name,
      identifier,
      paymentCount: item.entries.length,
      openingBalance,
      feesAndCharges: buildFeesAndChargesBreakdown(
        manualPaymentFeeEntries(item.entries),
      ),
    }));
  });

  groupUnmatchedIoPayments(ioRows, matchedIoRecipientIds).forEach((item) => {
    const commissionEntries = ioRows.filter(
      (entry) =>
        keyOf(ioRecipientUserId(entry)) === keyOf(item.recipientUserId),
    );
    rows.push(outputRow({
      category: "Unmatched IO Users",
      name: item.recipientName,
      identifier: item.recipientUserId,
      paymentCount: item.paymentCount,
      totalCommission: commissionTotal(
        commissionEntries,
        selectedMonth,
        "io",
      ),
    }));
  });

  return rows.sort(
    (left, right) =>
      left.Category.localeCompare(right.Category, "en-GB") ||
      left.Name.localeCompare(right.Name, "en-GB", { numeric: true }),
  );
};
